// Composite key for project requirement / deprecation / function-assignment
// maps, scoping each entry to the (derivedContext, skill) it was set in rather
// than the skill alone.
//
// Why: a skill can belong to multiple derivedContexts (e.g. `react` under both
// `api-docs-surface` and `studio-frontend`). Keying these demand-side maps by
// skill id alone made an edit in one derivedContext bleed into every other
// derivedContext that shared the skill — painting unrelated cells in the editor
// and mis-crediting the impact/diff maps. Keying by `comp::skill` isolates
// them while the skill stays a single shared catalog entity.
//
// The stored shape is unchanged — still `Record<roleId, Record<key, V>>` — so
// the plan payload schema and persistence are untouched. Legacy plans (keyed
// by bare skill id) are upgraded on read by `normalizeLegacyReqMap`.

/** Separator. Slugs match /^[a-z0-9][a-z0-9_-]*$/i, so `::` never collides. */
const SEP = '::';

export function reqKey(compId: string, skillId: string): string {
  return `${compId}${SEP}${skillId}`;
}

export function isCompositeKey(key: string): boolean {
  return key.includes(SEP);
}

/** Skill id portion of a (possibly legacy) key. */
export function reqKeySkillId(key: string): string {
  const i = key.indexOf(SEP);
  return i < 0 ? key : key.slice(i + SEP.length);
}

/**
 * Upgrade a legacy per-(role, skill) map to per-(role, comp::skill).
 *
 * Each legacy skill entry is distributed to every derivedContext that contains
 * that skill — faithfully preserving the old "applies everywhere the skill
 * appears" behaviour for existing saved plans, until they're re-saved (which
 * persists the scoped shape). Already-composite maps pass through untouched,
 * so this is idempotent and safe to call on both old and new payloads.
 */
export function normalizeLegacyReqMap<V>(
  map: Record<string, Record<string, V>> | undefined,
  skillToComps: Map<string, string[]>,
): Record<string, Record<string, V>> {
  const out: Record<string, Record<string, V>> = {};
  for (const [roleId, inner] of Object.entries(map ?? {})) {
    const o: Record<string, V> = {};
    for (const [key, value] of Object.entries(inner)) {
      if (isCompositeKey(key)) {
        o[key] = value; // already scoped
        continue;
      }
      const comps = skillToComps.get(key) ?? [];
      for (const compId of comps) o[reqKey(compId, key)] = value;
      // A legacy skill that maps to no current derivedContext is dropped — it
      // could not be rendered against any derivedContext row anyway.
    }
    out[roleId] = o;
  }
  return out;
}

/**
 * Is `(comp, skill, fn)` a "net-new" association — a function the project
 * assigned beyond the skill's catalog footprint?
 */
export function isNewFunction(
  compId: string,
  skillId: string,
  fnId: string,
  newSkillFunctions: Record<string, string[]> | undefined,
): boolean {
  return newSkillFunctions?.[reqKey(compId, skillId)]?.includes(fnId) ?? false;
}

/**
 * Whether a skill (in a derivedContext context) serves a function — either via
 * its catalog footprint or a project-assigned net-new association.
 */
export function skillServesFunction(
  compId: string,
  skillId: string,
  fnId: string,
  catalogSkillFns: Map<string, Set<string>>,
  newSkillFunctions?: Record<string, string[]>,
): boolean {
  if (catalogSkillFns.get(skillId)?.has(fnId)) return true;
  return isNewFunction(compId, skillId, fnId, newSkillFunctions);
}

/**
 * Flat-mode (per-skill) analog of `compServesFunctionWithNew`: does this
 * (comp, skill) cell light up for `fnId`?
 *
 * A derivedContext that declares no functions (a fresh overlay comp, or any
 * comp whose catalog footprint is empty) serves ALL functions — the same
 * fallback the grouped path relies on. Without it, the ungrouped Impact /
 * Diff grids render every such cell as N/A and the whole grid reads blank.
 * Comps that DO declare functions keep their per-skill footprint (+ net-new)
 * granularity.
 */
export function skillCellServesFunction(
  comp: { id: string; functions: string[] },
  skillId: string,
  fnId: string,
  catalogSkillFns: Map<string, Set<string>>,
  newSkillFunctions?: Record<string, string[]>,
): boolean {
  if (comp.functions.length === 0) return true;
  return skillServesFunction(
    comp.id,
    skillId,
    fnId,
    catalogSkillFns,
    newSkillFunctions,
  );
}

/**
 * Whether a derivedContext serves a function — any of its skills do (catalog or
 * net-new). A derivedContext that declares no functions yet (a fresh overlay
 * comp) serves all. Mirrors `compServesFunction` but folds in net-new
 * associations so the grouped Impact/Diff cell lights them too.
 */
export function compServesFunctionWithNew(
  comp: { id: string; functions: string[]; skills: Array<{ id: string }> },
  fnId: string,
  catalogSkillFns: Map<string, Set<string>>,
  newSkillFunctions?: Record<string, string[]>,
): boolean {
  if (comp.functions.length === 0) return true;
  return comp.skills.some((s) =>
    skillServesFunction(comp.id, s.id, fnId, catalogSkillFns, newSkillFunctions),
  );
}

/** Build skill id → derivedContext ids from a derivedContext list (base + overlay). */
export function buildSkillToComps(
  comps: Array<{ id: string; skills: Array<{ id: string }> }>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const c of comps) {
    for (const s of c.skills) {
      const arr = map.get(s.id);
      if (arr) arr.push(c.id);
      else map.set(s.id, [c.id]);
    }
  }
  return map;
}
